// Free / Premium data mode (D-152). Free = the built-in Yahoo aggregate,
// deliberately scoped to STOCKS only. Premium = "bring your own" live data/broker
// connections; the asset classes you can build with are the union of the classes
// covered by the providers you've actually connected (per-provider mapping —
// each DataProvider declares its `assetClasses`). With Premium selected but no
// connection established, nothing is available and the nav guard blocks building
// until you connect one (or flip back to Free).

import { storageGet, storageSet, onStorageChange } from "./storage.js";
import { allProviders } from "./data-providers/registry.js";
import type { DataProvider } from "./data-providers/types.js";
import type { AssetClass } from "./stock-directory.js";

export type DataMode = "free" | "premium";

const MODE_KEY = "dataMode";
// Policy: the free tier is stocks only (Yahoo covers more, but Free is scoped).
const FREE_CLASSES: AssetClass[] = ["Stocks"];

export async function getDataMode(): Promise<DataMode> {
  return (await storageGet<DataMode>(MODE_KEY, "free")) === "premium" ? "premium" : "free";
}

export async function setDataMode(mode: DataMode): Promise<void> {
  await storageSet(MODE_KEY, mode);
}

export function onDataModeChange(cb: (mode: DataMode) => void): () => void {
  return onStorageChange<DataMode>(MODE_KEY, (v) => cb(v === "premium" ? "premium" : "free"));
}

/** The BYO providers that currently hold credentials (i.e. are connected). */
export async function connectedProviders(): Promise<DataProvider[]> {
  const byo = allProviders.filter((p) => p.byo);
  const has = await Promise.all(byo.map((p) => p.byo!.getCreds().then((c) => c != null).catch(() => false)));
  return byo.filter((_, i) => has[i]);
}

/** Asset classes the user may build with, for the given mode (defaults to the
 * stored mode). Free → stocks; Premium → union of connected providers' classes
 * (empty when nothing is connected yet). */
export async function availableClasses(mode?: DataMode): Promise<AssetClass[]> {
  const m = mode ?? (await getDataMode());
  if (m === "free") return [...FREE_CLASSES];
  const set = new Set<AssetClass>();
  for (const p of await connectedProviders()) for (const cls of p.assetClasses) set.add(cls);
  return [...set];
}

/** Premium is "satisfied" (building/navigation allowed) once at least one live
 * connection is established. Free is always satisfied. */
export async function modeSatisfied(mode?: DataMode): Promise<boolean> {
  const m = mode ?? (await getDataMode());
  if (m === "free") return true;
  return (await connectedProviders()).length > 0;
}
